<template>
  <div class="freebets-round text-black animation-slide md:px-3">
    <Filters
      :options="options"
      :showActionButton="true"
      :search="search"
      :resetFilter="resetFilter"
      :resetPagination="resetPagination"
    />
    <div
      class="d-flex w-100 align-items-center justify-content-end px-2 gap-2 my-2"
    >
      <button
        @click="handleAddPlayer"
        class="inline-flex whitespace-nowrap group flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm transition-all hover:border-blue-300 hover:bg-blue-50 hover:text-blue-600 active:scale-95"
      >
        <UserPlusIcon class="icon block w-5 h-5" />
        Add player
      </button>
      <button
        @click="handleImportPlayer"
        class="inline-flex whitespace-nowrap flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-all hover:bg-blue-700 hover:shadow-md active:scale-95"
      >
        <DocumentPlusIcon class="icon block w-5 h-5" />
        Import players
      </button>
    </div>
    <div
      v-if="isLoading"
      class="flex-grow flex flex-col items-center justify-center p-20"
    >
      <Loader />
    </div>

    <div
      v-else
      class="table-container-custom shadow-sm rounded-2xl overflow-hidden border border-1 mb-3"
    >
      <DataTable
        :value="tableData"
        :lazy="true"
        :paginator="true"
        :rows="selectedLimit"
        :totalRecords="totalElements"
        :loading="isLoading"
        :first="(currentPage - 1) * selectedLimit"
        @page="onPage"
        @sort="onSort"
        :sortField="sortField"
        :sortOrder="sortOrder"
        :rowsPerPageOptions="[15, 25, 50, 100]"
        removableSort
        stripedRows
        scrollable
        scrollHeight="flex"
        responsiveLayout="stack"
        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
        class="custom-lazy-table"
      >
        <template #empty>
          <p class="text-center py-4 text-muted">
            No restricted players found.
          </p>
        </template>

        <Column
          field="player_id"
          header="Player ID"
          sortable
          class="font-bold text-slate-700"
        >
          <template #body="{ data }">
            <div class="flex items-center gap-2">
              <div class="w-1.5 h-1.5 rounded-full bg-emerald-400"></div>
              {{ data.player_id }}
            </div>
          </template>
        </Column>

        <Column field="created_at" header="Created At" sortable>
          <template #body="{ data }">
            <span class="text-slate-500 font-medium">{{
              formatDate(data.created_at)
            }}</span>
          </template>
        </Column>

        <Column field="updated_at" header="Updated At" sortable>
          <template #body="{ data }">
            <span class="text-slate-500 font-medium">{{
              formatDate(data.updated_at)
            }}</span>
          </template>
        </Column>

        <Column
          header="Action"
          headerStyle="width: 5rem; text-align: center"
          bodyStyle="text-align: center"
        >
          <template #body="{ data }">
            <Button
              icon="pi pi-trash"
              rounded
              text
              severity="danger"
              v-tooltip.top="'Remove Rain'"
              @click="removePlayerFromFreebet(data)"
              class="hover:bg-red-50 rounded-full"
            />
          </template>
        </Column>
      </DataTable>
    </div>
  </div>

  <ImportPlayerToFreebet
    v-if="showImportPlayer"
    :detail="detail"
    :resources="'campaigns'"
    @import-player-modal-open="handleImportPlayer"
  />

  <AddPlayerToCampaign
    v-if="showAddPlayerModal"
    :detail="detail"
    @add-player-modal-open="handleAddPlayer"
  />
</template>

<script>
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import Filters from "../table/Filters.vue";
import { getFetch, deleteRequest } from "@/api/axiosClient.js";
import Swal from "sweetalert2";
import { UserPlusIcon, DocumentPlusIcon } from "@heroicons/vue/24/outline";
import ImportPlayerToFreebet from "../ImportPlayerToFreebet.vue";
import AddPlayerToCampaign from "./AddPlayerToCampaign.vue";
import { Button } from "primevue";
import Loader from "../Loader.vue";

export default {
  name: "RainRestrictions",
  props: {
    freebet_id: { type: String, required: true },
    skinFilt: { type: Number, required: true },
    detail: { type: Object, required: true },
  },
  components: {
    DataTable,
    Column,
    Filters,
    UserPlusIcon,
    DocumentPlusIcon,
    AddPlayerToCampaign,
    ImportPlayerToFreebet,
    Button,
    Loader,
  },
  data() {
    return {
      currentPage: 1,
      tableData: [],
      isLoading: false,
      totalElements: 0,
      selectedLimit: 15,
      sortField: "created_at",
      sortOrder: -1,
      player_id: "",
      showImportPlayer: false,
      showAddPlayerModal: false,
      options: [
        {
          label: "Player ID",
          type: "text",
          placeholder: "Search for Player ID",
          property: "player_id",
        },
        {
          label: "Campaign ID",
          type: "text",
          property: "freebet_id",
          disabled: true,
        },
      ],
    };
  },
  async mounted() {
    await this.search();
  },
  methods: {
    // PrimeVue Lazy Events
    async onPage(event) {
      this.currentPage = event.page + 1;
      this.selectedLimit = event.rows;
      await this.search();
    },
    async onSort(event) {
      this.sortField = event.sortField;
      this.sortOrder = event.sortOrder;
      await this.search();
    },

    formatDate(v) {
      if (!v) return "-";
      const d = new Date(v);
      return `${String(d.getDate()).padStart(2, "0")}/${String(
        d.getMonth() + 1,
      ).padStart(2, "0")}/${d.getFullYear()}`;
    },

    // UI Handlers
    async handleAddPlayer() {
      this.showAddPlayerModal = !this.showAddPlayerModal;
    },
    async handleImportPlayer() {
      this.showImportPlayer = !this.showImportPlayer;
      document.body.style.overflow = this.showImportPlayer ? "hidden" : "";
    },

    // Filter Handlers
    async resetPagination() {
      this.currentPage = 1;
    },
    async resetFilter() {
      this.currentPage = 1;
      this.selectedLimit = 15;
      this.player_id = "";
      await this.search();
    },

    // API Actions
    async removePlayerFromFreebet(row) {
      const result = await Swal.fire({
        title: "Are you sure?",
        text: "Remove the player from the Rain campaign.",
        icon: "warning",
        showCancelButton: true,
        cancelButtonColor: "#f1f5f9",
        confirmButtonText: "Yes, remove it!",
        customClass: {
          confirmButton: "!text-white",
          cancelButton: "!text-slate-500",
        },
      });

      if (result.isConfirmed) {
        try {
          this.isLoading = true;
          await deleteRequest(`campaigns/restricted/${this.freebet_id}`, {
            player_id: row.player_id,
          });
          await Swal.fire(
            "Deleted!",
            "The player has been removed.",
            "success",
          );
          await this.search(); // Refresh table
        } catch (error) {
          Swal.fire("Error", "Failed to remove the player.", "error");
        } finally {
          this.isLoading = false;
        }
      }
    },

    async search() {
      this.isLoading = true;
      try {
        const params = {
          skin_id: this.skinFilt,
          page: this.currentPage,
          limit: this.selectedLimit,
          sortField: this.sortField,
          sortOrder: this.sortOrder === 1 ? "asc" : "desc",
        };

        if (this.player_id) params.player_id = this.player_id.trim();

        const data = await getFetch(
          `campaigns/restricted/${this.freebet_id}`,
          params,
        );

        this.tableData = data.data ?? [];
        this.totalElements = data.totalItems || 0;

        setTimeout(() => {
          window.scrollTo({ top: 0, behavior: "smooth" });
        }, 250);
      } catch (error) {
        console.error("Fetch Error:", error);
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>
